import React, {Suspense} from 'react';

import * as factories from 'main/factories/pages';
import {PrivateRoutes} from 'main/proxies';
import {
	BeneficiaryTokenWrapper,
	BookingWrapper,
	BusinessAccessPermission,
	CanceledContractFindOutMore,
	FranchisesTableWrapper,
	HealthCardPage,
	ProfileWrapper,
} from 'presentation/pages';

import {Loading} from 'presentation/components/Loading/Loading';
import {
	BookingAuthEnum,
	BusinessPermissionFlowEnum,
	CancelledContractEnum,
	CommunicationsEnum,
	ConsultProtocolsEnum,
	EmergencyTelehealthEnum,
	ExamBookingEnum,
	ExamResultsEnum,
	FavoritesEnum,
	FiveStarsRatingEnum,
	FranchisesTableEnum,
	GuidesEnum,
	HealthBookingEnum,
	HealthCardEnum,
	HelpCenterEnum,
	HomeEnum,
	MyAppointmentsEnum,
	MyHospitalizationFlowEnum,
	NotificationsEnum,
	OdontoBookingEnum,
	OperationalCostEnum,
	PerksEnum,
	ProfileEnum,
	RequestSchedule,
	ServicesEnum,
	TelehealthBookingEnum,
	TokenEnum,
} from 'presentation/constants';

import {BeneficiaryToken} from 'presentation/pages/private/beneficiary-token/components/BeneficiaryToken';
import {FlowEnum} from 'presentation/pages/private/booking/constants';
import {InitialStep} from 'presentation/pages/private/booking/pages';
import {RequestScheduleWrapper} from 'presentation/pages/private/booking/pages/RequestSchedule';
import {CommunicationsLayout} from 'presentation/pages/private/communications/components/CommunicationLayout';
import {ConsultProtocolsWrapper} from 'presentation/pages/private/consult-protocols';
import {ProtocolsConsult} from 'presentation/pages/private/consult-protocols/pages/ProtocolsConsult';

import {HealthGuideCities} from 'presentation/modules/guide/health/ui/pages/HealthGuideCities';
import {HealthGuideDistricts} from 'presentation/modules/guide/health/ui/pages/HealthGuideDistricts';
import {HealthGuideStates} from 'presentation/modules/guide/health/ui/pages/HealthGuideStates';
import {OdontoGuideCities} from 'presentation/modules/guide/odonto/ui/pages/OdontoGuideCities';
import {OdontoGuideDistricts} from 'presentation/modules/guide/odonto/ui/pages/OdontoGuideDistricts';
import {OdontoGuideStates} from 'presentation/modules/guide/odonto/ui/pages/OdontoGuideStates';
import {MyAppointments} from 'presentation/modules/myAppointments/ui/pages/MyAppointments';
import {FiveStarsLayout} from 'presentation/pages/private/five-stars/components';
import {FranchisesTable} from 'presentation/pages/private/franchises-table/pages/FranchisesTable';
import {MyHospitalizationLayout} from 'presentation/pages/private/my-hospitalization/layout';
import {MyHospitalizationDoctorVisits} from 'presentation/pages/private/my-hospitalization/pages/DoctorVisits';
import {MyHospitalizationExams} from 'presentation/pages/private/my-hospitalization/pages/Exams';
import {MyHospitalizationGeneralOrientations} from 'presentation/pages/private/my-hospitalization/pages/GeneralOrientations';
import {MyHospitalizationHome} from 'presentation/pages/private/my-hospitalization/pages/Home';
import {MyHospitalizationInformation} from 'presentation/pages/private/my-hospitalization/pages/Informations';
import {MyHospitalizationOpinions} from 'presentation/pages/private/my-hospitalization/pages/Opinions';
import {MyHospitalizationPatientData} from 'presentation/pages/private/my-hospitalization/pages/PatientData';
import {MyHospitalizationPrescriptions} from 'presentation/pages/private/my-hospitalization/pages/Prescriptions';
import {MyHospitalizationProcedures} from 'presentation/pages/private/my-hospitalization/pages/Procedures';
import {OperationalCostWrapper} from 'presentation/pages/private/operational-cost';
import {OperationalCostNoPassword} from 'presentation/pages/private/operational-cost/pages';
import {Perks} from 'presentation/pages/private/perks';
import {ChangePassword} from 'presentation/pages/private/profile/change-password';
import {
	DataSharing,
	DataSharingFeatures,
} from 'presentation/pages/private/profile/data-sharing/pages';
import {Profile} from 'presentation/pages/private/profile/profile-page';
import {SendYourMessage} from 'presentation/pages/private/send-your-message';
import {translations} from 'presentation/translations-new';
import {RouteObject} from 'react-router-dom';

import {MakeHealthGuide, MakeOdontoGuide} from 'main/factories/pages';
import {MakeEmergencyTelehealthJourney} from 'main/factories/pages/private/booking/emergency-telehealth-journey-factory';
import {MakeExamConfirmationBooking} from 'main/factories/pages/private/booking/exam-confirmation-booking-factory';
import {MakeHealthBooking} from 'main/factories/pages/private/booking/health-booking-factory';
import {MakeHealthConfirmationBooking} from 'main/factories/pages/private/booking/health-confirmation-booking-factory';
import {MakeOdontoConfirmationBooking} from 'main/factories/pages/private/booking/odonto-confirmation-booking-factory';
import {MakeTelehealthBooking} from 'main/factories/pages/private/booking/telehealth-booking-factory';
import {MakeTelehealthConfirmationBooking} from 'main/factories/pages/private/booking/telehealth-confirmation-booking-factory';
import {getFinancialRoutes} from 'modules/financial/routes/index';
import type {FinancialArchitectureFlags} from 'modules/financial/routes/types';
import {ExamAuthorization} from 'presentation/modules/booking/exam/ui/pages/ExamAuthorization';
import {ProcedureSelectionWrapper} from 'presentation/modules/booking/exam/ui/pages/ProcedureSelection';
import {FavoriteDoctors} from 'presentation/modules/booking/favoriteDoctors';
import {LocalitiesSelection} from 'presentation/modules/booking/shared/ui/pages/LocalitiesSelection';
import {ExamResultsWrapper} from 'presentation/modules/examResults/ui/pages/ExamResults';
import {FavoriteAddressesAdd} from 'presentation/pages/private/profile/FavoriteAddresses/Add';
import {FavoriteAddressesList} from 'presentation/pages/private/profile/FavoriteAddresses/List';
import {FavoriteAddressesEdit} from 'presentation/pages/private/profile/FavoriteAddresses/Edit';
import {FavoriteAddressesWrapper} from 'presentation/pages/private/profile/FavoriteAddresses/FavoriteAddressesWrapper';
import {HealthGuideFavoritePlacesAdd} from 'presentation/modules/guide/health/ui/pages/HealthGuideFavoritePlaces/Add';
import {HealthGuideFavoritePlacesEdit} from 'presentation/modules/guide/health/ui/pages/HealthGuideFavoritePlaces/Edit';
import {HealthGuideFavoritePlacesList} from 'presentation/modules/guide/health/ui/pages/HealthGuideFavoritePlaces/List';
import {NotificationsAreaWrapper} from 'presentation/pages/private/notifications-area';
import {WaitingExamResults} from 'presentation/modules/examResults/ui/pages/WaitingExamResults';
import {ReleasedExamResults} from 'presentation/modules/examResults/ui/pages/ReleasedExamResults';
import {ExamResultsLayoutContainer} from 'presentation/modules/examResults/ui/components/ExamResultsLayoutContainer';
import {ExamBooking} from 'presentation/modules/booking/exam/ui/pages/ExamBooking';
import {ServicePointsAddresses} from 'presentation/pages/private/service-points';

const {loading} = translations['pt-br'].commons;

const MakeHelp = React.lazy(() =>
	import('main/factories/pages').then(m => ({default: m.MakeHelp})),
);
const MakeMyAppointments = React.lazy(() =>
	import('main/factories/pages').then(m => ({default: m.MakeMyAppointments})),
);
const MakeMyAppointmentsHistory = React.lazy(() =>
	import('main/factories/pages').then(m => ({default: m.MakeMyAppointmentsHistory})),
);
const MakeMyAppointmentsHistoryDetails = React.lazy(() =>
	import('main/factories/pages').then(m => ({default: m.MakeMyAppointmentsHistoryDetails})),
);
const MakeOperationalCost = React.lazy(() =>
	import('main/factories/pages').then(m => ({default: m.MakeOperationalCost})),
);

export function getPrivateRoutes(financialFlags: FinancialArchitectureFlags) {
	const privateRoutes: RouteObject[] = [
		{
			element: <PrivateRoutes />,
			children: [
				...getFinancialRoutes(financialFlags),
				{
					path: HomeEnum.HOME_PAGE,
					element: <factories.MakeHomePage />,
				},
				{
					path: HomeEnum.EARLY_ACCESS,
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<factories.MakeEarlyAccess />
						</Suspense>
					),
				},
				{
					path: HomeEnum.ACCESS_PORTAL,
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<factories.MakeAccessPortal />
						</Suspense>
					),
				},
				{
					element: <MyAppointments />,
					children: [
						{
							path: MyAppointmentsEnum.MY_APPOINTMENTS,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<MakeMyAppointments />
								</Suspense>
							),
						},
						{
							path: MyAppointmentsEnum.MY_APPOINTMENTS_DETAILS_HEALTH,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<factories.MakeMyAppointmentsDetails
										flowType={FlowEnum.HEALTH}
									/>
								</Suspense>
							),
						},
						{
							path: MyAppointmentsEnum.MY_APPOINTMENTS_DETAILS_TELEHEALTH,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<factories.MakeMyAppointmentsDetails
										flowType={FlowEnum.TELEHEALTH}
									/>
								</Suspense>
							),
						},
						{
							path: MyAppointmentsEnum.MY_APPOINTMENTS_DETAILS_ODONTO,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<factories.MakeMyAppointmentsDetails
										flowType={FlowEnum.ODONTO}
									/>
								</Suspense>
							),
						},
						{
							path: MyAppointmentsEnum.MY_APPOINTMENTS_DETAILS_EXAM,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<factories.MakeMyAppointmentsDetails flowType={FlowEnum.EXAM} />
								</Suspense>
							),
						},
						{
							path: MyAppointmentsEnum.MY_APPOINTMENTS_DETAILS_SURGERY,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<factories.MakeMyAppointmentsDetails
										flowType={FlowEnum.SURGERY}
									/>
								</Suspense>
							),
						},
						{
							path: MyAppointmentsEnum.MY_APPOINTMENTS_HISTORY,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<MakeMyAppointmentsHistory />
								</Suspense>
							),
						},
						{
							path: MyAppointmentsEnum.MY_APPOINTMENTS_HISTORY_DETAILS,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<MakeMyAppointmentsHistoryDetails />
								</Suspense>
							),
						},
						{
							path: MyAppointmentsEnum.MY_APPOINTMENTS_HISTORY_SURGERY_DETAILS,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<MakeMyAppointmentsHistoryDetails isSurgeryPath />
								</Suspense>
							),
						},
					],
				},
				{
					path: GuidesEnum.SERVICE_NETWORK_ODONTO,
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<MakeOdontoGuide />
						</Suspense>
					),
				},
				{
					path: GuidesEnum.SERVICE_NETWORK_HEALTH_FLOW_UF,
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<HealthGuideStates />
						</Suspense>
					),
				},
				{
					path: GuidesEnum.SERVICE_NETWORK_HEALTH_FLOW_CITY,
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<HealthGuideCities />
						</Suspense>
					),
				},
				{
					path: GuidesEnum.SERVICE_NETWORK_HEALTH_FLOW_DISTRICT,
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<HealthGuideDistricts />
						</Suspense>
					),
				},
				{
					path: GuidesEnum.SERVICE_NETWORK_ODONTO_FLOW_UF,
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<OdontoGuideStates />
						</Suspense>
					),
				},
				{
					path: GuidesEnum.SERVICE_NETWORK_ODONTO_FLOW_CITY,
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<OdontoGuideCities />
						</Suspense>
					),
				},
				{
					path: GuidesEnum.SERVICE_NETWORK_ODONTO_FLOW_DISTRICT,
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<OdontoGuideDistricts />
						</Suspense>
					),
				},
				{
					path: GuidesEnum.SERVICE_NETWORK_HEALTH_FLOW_FAVORITE_PLACES_LIST,
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<HealthGuideFavoritePlacesList />
						</Suspense>
					),
				},
				{
					path: GuidesEnum.SERVICE_NETWORK_HEALTH_FLOW_FAVORITE_PLACES_ADD,
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<HealthGuideFavoritePlacesAdd />
						</Suspense>
					),
				},
				{
					path: GuidesEnum.SERVICE_NETWORK_HEALTH_FLOW_FAVORITE_PLACES_EDIT,
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<HealthGuideFavoritePlacesEdit />
						</Suspense>
					),
				},
				{
					path: GuidesEnum.SERVICE_NETWORK_HEALTH,
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<MakeHealthGuide />
						</Suspense>
					),
				},
				{
					path: ServicesEnum.SERVICES,
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<factories.MakeServicesPage />
						</Suspense>
					),
				},
				{
					path: HealthCardEnum.HEALTH_CARD,
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<HealthCardPage />
						</Suspense>
					),
				},
				{
					path: PerksEnum.PERKS,
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<Perks />
						</Suspense>
					),
				},
				{
					element: <ProfileWrapper />,
					children: [
						{
							path: ProfileEnum.PROFILE,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<Profile />
								</Suspense>
							),
						},
						{
							path: ProfileEnum.CHANGE_PASSWORD,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<ChangePassword />
								</Suspense>
							),
						},
						{
							path: ProfileEnum.DATA_SHARING,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<DataSharing />
								</Suspense>
							),
						},
						{
							path: ProfileEnum.DATA_SHARING_FEATURES,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<DataSharingFeatures />
								</Suspense>
							),
						},
						{
							element: <FavoriteAddressesWrapper />,
							children: [
								{
									path: ProfileEnum.FAVORITE_ADDRESSES_LIST,
									element: (
										<Suspense
											fallback={<Loading text={loading} minHeight="100%" />}>
											<FavoriteAddressesList />
										</Suspense>
									),
								},
								{
									path: ProfileEnum.FAVORITE_ADDRESSES_ADD,
									element: (
										<Suspense
											fallback={<Loading text={loading} minHeight="100%" />}>
											<FavoriteAddressesAdd />
										</Suspense>
									),
								},
								{
									path: ProfileEnum.FAVORITE_ADDRESSES_EDIT,
									element: (
										<Suspense
											fallback={<Loading text={loading} minHeight="100%" />}>
											<FavoriteAddressesEdit />
										</Suspense>
									),
								},
							],
						},
					],
				},
				{
					element: <OperationalCostWrapper />,
					children: [
						{
							path: OperationalCostEnum.OPERATIONAL_COST,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<OperationalCostNoPassword />
								</Suspense>
							),
						},
						{
							path: OperationalCostEnum.OPERATIONAL_COST_NEED_PASSWORD,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<MakeOperationalCost />
								</Suspense>
							),
						},
					],
				},
				{
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<BookingWrapper />
						</Suspense>
					),
					children: [
						{
							path: EmergencyTelehealthEnum.EMERGENCY_TELEHEALTH_FLOW,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<MakeEmergencyTelehealthJourney />
								</Suspense>
							),
						},
						{
							path: TelehealthBookingEnum.TELEHEALTH_BOOKING_UF,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<factories.MakeUfSelection flowType={FlowEnum.TELEHEALTH} />
								</Suspense>
							),
						},
						{
							path: TelehealthBookingEnum.TELEHEALTH_BOOKING_CITY,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<factories.MakeCitySelection flowType={FlowEnum.TELEHEALTH} />
								</Suspense>
							),
						},
						{
							path: TelehealthBookingEnum.TELEHEALTH_BOOKING_INITIAL,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<InitialStep flowType={FlowEnum.TELEHEALTH} />
								</Suspense>
							),
						},
						{
							path: TelehealthBookingEnum.TELEHEALTH_BOOKING_CONFIRMATION,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<MakeTelehealthConfirmationBooking />
								</Suspense>
							),
						},
						{
							path: TelehealthBookingEnum.TELEHEALTH_BOOKING_FLOW,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<MakeTelehealthBooking />
								</Suspense>
							),
						},
						{
							path: HealthBookingEnum.HEALTH_BOOKING_UF,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<factories.MakeUfSelection flowType={FlowEnum.HEALTH} />
								</Suspense>
							),
						},
						{
							path: HealthBookingEnum.HEALTH_BOOKING_CITY,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<factories.MakeCitySelection flowType={FlowEnum.HEALTH} />
								</Suspense>
							),
						},
						{
							path: HealthBookingEnum.HEALTH_BOOKING_INITIAL,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<InitialStep flowType={FlowEnum.HEALTH} />
								</Suspense>
							),
						},
						{
							path: HealthBookingEnum.HEALTH_BOOKING_CONFIRMATION,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<MakeHealthConfirmationBooking />
								</Suspense>
							),
						},
						{
							path: HealthBookingEnum.HEALTH_BOOKING_FLOW,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<MakeHealthBooking />
								</Suspense>
							),
						},
						{
							path: OdontoBookingEnum.ODONTO_BOOKING_UF,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<factories.MakeUfSelection flowType={FlowEnum.ODONTO} />
								</Suspense>
							),
						},
						{
							path: OdontoBookingEnum.ODONTO_BOOKING_CITY,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<factories.MakeCitySelection flowType={FlowEnum.ODONTO} />
								</Suspense>
							),
						},
						{
							path: HealthBookingEnum.HEALTH_BOOKING_LOCALITY,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<LocalitiesSelection flowType={FlowEnum.HEALTH} />
								</Suspense>
							),
						},
						{
							path: OdontoBookingEnum.ODONTO_BOOKING_INITIAL,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<InitialStep flowType={FlowEnum.ODONTO} />
								</Suspense>
							),
						},
						{
							path: OdontoBookingEnum.ODONTO_BOOKING_CONFIRMATION,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<MakeOdontoConfirmationBooking />
								</Suspense>
							),
						},
						{
							path: OdontoBookingEnum.ODONTO_BOOKING_FLOW,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<factories.MakeOdontoBooking />
								</Suspense>
							),
						},
						{
							path: ExamBookingEnum.EXAM_BOOKING_UF,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<factories.MakeUfSelection flowType={FlowEnum.EXAM} />
								</Suspense>
							),
						},
						{
							path: ExamBookingEnum.EXAM_BOOKING_LOCALITY,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<LocalitiesSelection flowType={FlowEnum.EXAM} />
								</Suspense>
							),
						},
						{
							path: ExamBookingEnum.EXAM_BOOKING_FLOW,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<ExamBooking />
								</Suspense>
							),
						},
						{
							path: ExamBookingEnum.EXAM_BOOKING_PROCEDURE_SELECTION,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<ProcedureSelectionWrapper />
								</Suspense>
							),
						},
						{
							path: BookingAuthEnum.EXAM_AUTHORIZATION,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<ExamAuthorization />
								</Suspense>
							),
						},
						{
							path: ExamBookingEnum.EXAM_BOOKING_CONFIRMATION,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<MakeExamConfirmationBooking />
								</Suspense>
							),
						},
						{
							path: RequestSchedule.REQUEST,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<RequestScheduleWrapper />
								</Suspense>
							),
						},
					],
				},
				{
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<ExamResultsWrapper />
						</Suspense>
					),
					children: [
						{
							path: ExamResultsEnum.EXAM_RESULTS,
							element: (
								<ExamResultsLayoutContainer>
									<ReleasedExamResults />
								</ExamResultsLayoutContainer>
							),
						},
						{
							path: ExamResultsEnum.EXAM_RESULTS_WAITING,
							element: (
								<ExamResultsLayoutContainer>
									<WaitingExamResults />
								</ExamResultsLayoutContainer>
							),
						},
					],
				},
				{
					path: HelpCenterEnum.HELP,
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<MakeHelp />
						</Suspense>
					),
				},
				{
					path: FiveStarsRatingEnum.FIVESTARS_RATING,
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<FiveStarsLayout />
						</Suspense>
					),
				},
				{
					path: HelpCenterEnum.SEND_MESSAGE,
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<SendYourMessage />
						</Suspense>
					),
				},
				{
					path: HelpCenterEnum.SERVICE_POINTS_ADDRESSES,
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<ServicePointsAddresses />
						</Suspense>
					),
				},
				{
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<BeneficiaryTokenWrapper />
						</Suspense>
					),
					children: [
						{
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<BeneficiaryToken />
								</Suspense>
							),
							path: TokenEnum.TOKEN,
						},
					],
				},
				{
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<FranchisesTableWrapper />
						</Suspense>
					),
					children: [
						{
							path: FranchisesTableEnum.FRANCHISES_TABLE,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<FranchisesTable />
								</Suspense>
							),
						},
					],
				},
				{
					path: ConsultProtocolsEnum.CONSULT_PROTOCOLS,
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<ConsultProtocolsWrapper />
						</Suspense>
					),
					children: [
						{
							path: ConsultProtocolsEnum.CONSULT_PROTOCOLS,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<ProtocolsConsult />
								</Suspense>
							),
						},
					],
				},
				{
					path: CancelledContractEnum.FIND_OUT_MORE,
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<CanceledContractFindOutMore />
						</Suspense>
					),
				},
				{
					path: CommunicationsEnum.COMMUNICATIONS,
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<CommunicationsLayout />
						</Suspense>
					),
				},
				{
					path: BusinessPermissionFlowEnum.ACCESS_PERMISSION,
					element: <BusinessAccessPermission />,
				},
				{
					path: FavoritesEnum.FAVORITE_DOCTORS,
					element: <FavoriteDoctors />,
				},
				{
					element: <MyHospitalizationLayout />,
					children: [
						{
							path: MyHospitalizationFlowEnum.HOME,
							element: <MyHospitalizationHome />,
						},
						{
							path: MyHospitalizationFlowEnum.GENERAL_ORIENTATIONS,
							element: <MyHospitalizationGeneralOrientations />,
						},
						{
							path: MyHospitalizationFlowEnum.INFORMATION,
							element: <MyHospitalizationInformation />,
						},
						{
							path: MyHospitalizationFlowEnum.OPINIONS,
							element: <MyHospitalizationOpinions />,
						},
						{
							path: MyHospitalizationFlowEnum.PATIENT_DATA,
							element: <MyHospitalizationPatientData />,
						},
						{
							element: <MyHospitalizationExams />,
							children: [
								{
									path: MyHospitalizationFlowEnum.EXAMS,
								},
								{
									path: MyHospitalizationFlowEnum.REQUESTED_EXAMS,
								},
							],
						},
						{
							path: MyHospitalizationFlowEnum.DOCTOR_VISITS,
							element: <MyHospitalizationDoctorVisits />,
						},
						{
							path: MyHospitalizationFlowEnum.PROCEDURES,
							element: <MyHospitalizationProcedures />,
						},
						{
							path: MyHospitalizationFlowEnum.PRESCRIPTIONS,
							element: <MyHospitalizationPrescriptions />,
						},
					],
				},
				{
					path: NotificationsEnum.NOTIFICATIONS,
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<NotificationsAreaWrapper />
						</Suspense>
					),
				},
			],
		},
	];
	return privateRoutes;
}
